/**
 * Attaches the API's `sentenceTranslations` (one English line per Chinese
 * sentence, in reading order) to the word objects the reader renders.
 *
 * Pairing is by text, not by paragraph index: we walk the rendered words and
 * the sentence texts character by character (whitespace ignored), and each
 * word joins the sentence its first character falls in. That is independent
 * of paragraph boundaries, so the backend's artificial `</p><p>` cuts in
 * single-paragraph stories don't matter. Any disagreement means the story
 * text changed since the sentences were generated: return null and the reader
 * falls back to paragraph English.
 */

export interface SentenceTranslation {
  paragraph: number
  text: string
  en: string
}

export interface WordRef {
  /** Paragraph index as LeftStory numbers them (DOM id `WORD-<p><i>`). */
  p: number
  /** Index of the word object within that paragraph. */
  i: number
}

export interface SentenceRow {
  sentence: number
  text: string
  en: string
  words: WordRef[]
  /** The row's first word opens a new paragraph: the reader adds paragraph spacing. */
  startsParagraph: boolean
}

export interface AttachedSentences {
  rows: SentenceRow[]
  /** DOM word id (`WORD-<p><i>`) to sentence index. */
  sentenceByWordId: Map<string, number>
}

const TAG_REGEX = /<[^>]+>/g
const WHITESPACE_REGEX = /\s/

const isParagraphToken = (word: string) => word.includes('<p>') || word.includes('</p>')

/** LeftStory's paragraph split of arContentWithTags: `<p>`-style tokens start
 *  a new paragraph (except the very first token, which stays in paragraph 0),
 *  so word indices, and therefore DOM ids, line up exactly. */
export function splitIntoParagraphs<T extends { word: string }>(storyContent: T[]): T[][] {
  const result: T[][] = [[]]
  storyContent.forEach((wordObj, index) => {
    if (isParagraphToken(wordObj.word) && index) {
      result.push([])
    } else {
      result[result.length - 1].push(wordObj)
    }
  })
  return result
}

export const wordDomId = (p: number, i: number) => `WORD-${p}${i}`

export function attachSentences(
  paragraphs: { word: string }[][],
  sentenceTranslations: SentenceTranslation[] | null | undefined,
): AttachedSentences | null {
  if (!Array.isArray(sentenceTranslations) || !sentenceTranslations.length) return null

  const stream: string[] = []
  const owner: number[] = []
  sentenceTranslations.forEach((sentence, index) => {
    for (const char of sentence?.text || '') {
      if (WHITESPACE_REGEX.test(char)) continue
      stream.push(char)
      owner.push(index)
    }
  })

  const rows: SentenceRow[] = sentenceTranslations.map((sentence, index) => ({
    sentence: index,
    text: sentence.text,
    en: (sentence.en || '').trim(),
    words: [],
    startsParagraph: false,
  }))
  const sentenceByWordId = new Map<string, number>()

  let pos = 0
  for (let p = 0; p < paragraphs.length; p++) {
    for (let i = 0; i < paragraphs[p].length; i++) {
      const raw = (paragraphs[p][i]?.word || '').replace(TAG_REGEX, '')
      let first = -1
      for (const char of raw) {
        if (WHITESPACE_REGEX.test(char)) continue
        if (stream[pos] !== char) return null
        if (first < 0) first = pos
        pos++
      }
      // Words with no visible characters (tags, stray spaces) render nothing
      // or an invisible span: keep them with the sentence in progress.
      const sentence = first >= 0 ? owner[first] : owner[Math.min(pos, owner.length - 1)]
      if (sentence === undefined) return null
      rows[sentence].words.push({ p, i })
      if (first >= 0) sentenceByWordId.set(wordDomId(p, i), sentence)
    }
  }
  if (pos !== stream.length) return null

  let lastParagraph = -1
  for (const row of rows) {
    if (!row.words.length || !row.en) return null
    const firstVisible = row.words.find(({ p, i }) => sentenceByWordId.get(wordDomId(p, i)) === row.sentence) || row.words[0]
    row.startsParagraph = lastParagraph >= 0 && firstVisible.p !== lastParagraph
    lastParagraph = row.words[row.words.length - 1].p
  }
  return { rows, sentenceByWordId }
}
